import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Activity, ArrowRight, Bell, Clock3, FileImage, HelpCircle, LogIn, UserPlus, Users } from 'lucide-react';
import { admin } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const relativeDate = value => {
  if (!value) return 'Never';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const Empty = ({ children }) => <p className="py-8 text-center text-sm text-gray-400">{children}</p>;

const personName = user => user?.contactName || user?.email || user?.companyName || 'Unnamed user';
const secondaryCompany = user => user?.companyName && user.companyName !== personName(user) ? user.companyName : null;

const InfoTip = ({ text }) => (
  <span className="group relative inline-flex" tabIndex={0} aria-label={text}>
    <HelpCircle size={15} className="cursor-help text-gray-400" />
    <span role="tooltip" className="pointer-events-none absolute left-1/2 top-6 z-20 hidden w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block group-focus:block">{text}</span>
  </span>
);

const SectionHeading = ({ title, subtitle, help, icon: Icon }) => (
  <div className="flex items-start justify-between gap-3">
    <div><div className="flex items-center gap-1.5"><h2 className="font-semibold text-gray-900">{title}</h2><InfoTip text={help} /></div><p className="text-xs text-gray-500">{subtitle}</p></div>
    {Icon && <Icon size={19} className="text-gray-400" />}
  </div>
);

const AdminDashboard = () => {
  const { isSuperAdmin } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await admin.getDashboard({ scope: isSuperAdmin ? 'all' : 'mine' });
        setDashboard(response.data);
      } catch (error) {
        console.error('Error fetching admin dashboard:', error);
        toast.error('Failed to load the admin dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isSuperAdmin]);

  const markNotificationsRead = async () => {
    try {
      await admin.markNotificationsRead();
      setDashboard(current => ({
        ...current,
        summary: { ...current.summary, unreadNotifications: 0 },
        notifications: current.notifications.map(item => ({ ...item, readAt: item.readAt || new Date().toISOString() }))
      }));
    } catch (error) {
      toast.error('Could not mark notifications as read');
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500/30 border-t-primary-500" /></div>;
  }

  const summary = dashboard?.summary || {};
  const cards = [
    { label: isSuperAdmin ? 'All users' : 'My assigned users', value: summary.totalUsers || 0, note: `${summary.active30 || 0} active in 30 days`, help: isSuperAdmin ? 'All non-admin customer accounts in the Hub.' : 'Customer accounts currently assigned to you.', to: '/admin/users', icon: Users, tone: 'bg-primary-100 text-primary-600' },
    { label: 'New users', value: summary.newUsers || 0, note: 'Joined in the last 30 days', help: 'Accounts created during the past 30 days.', to: '/admin/users?segment=new', icon: UserPlus, tone: 'bg-green-100 text-green-600' },
    { label: 'Needs follow-up', value: summary.inactive30 || 0, note: `${summary.inactive60 || 0} inactive 60+ days`, help: 'Users who have not logged in during the past 30 days. Click to open the complete follow-up list.', to: '/admin/users?segment=inactive30', icon: Clock3, tone: 'bg-amber-100 text-amber-600' },
    { label: 'Never logged in', value: summary.neverLoggedIn || 0, note: 'Good candidates for outreach', help: 'Accounts with no successful login recorded yet.', to: '/admin/users?segment=never', icon: LogIn, tone: 'bg-purple-100 text-purple-600' }
  ];

  return (
    <div className="animate-fade-in">
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">{isSuperAdmin ? 'Systemwide users, follow-ups, and content momentum at a glance' : 'Your team, follow-ups, and content momentum at a glance'}</p>
        </div>
        <Link to="/admin/analytics" className="btn-outline inline-flex items-center gap-2 self-start">Open detailed analytics <ArrowRight size={17} /></Link>
      </div>

      {summary.unassignedUsers > 0 && (
        <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <p className="text-sm"><strong>{summary.unassignedUsers} user{summary.unassignedUsers === 1 ? '' : 's'}</strong> need an administrator assignment.</p>
          <Link to="/admin/users?owner=unassigned" className="shrink-0 text-sm font-semibold underline">Assign now</Link>
        </div>
      )}

      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(card => {
          const Icon = card.icon;
          return (
            <Link key={card.label} to={card.to} className="card group block p-5 transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-400">
              <div className="flex items-start justify-between gap-3">
                <div><div className="flex items-center gap-1.5"><p className="text-sm text-gray-500">{card.label}</p><InfoTip text={card.help} /></div><p className="mt-1 text-3xl font-bold text-gray-900">{card.value}</p><p className="mt-1 text-xs text-gray-500">{card.note}</p></div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.tone}`}><Icon size={22} /></div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="card p-6">
          <div className="mb-4"><SectionHeading title="Top users this month" subtitle="Ranked by exports and logins" help="A recent-activity ranking weighted toward completed flyer exports, with logins used as an additional engagement signal." icon={Activity} /></div>
          {dashboard?.topUsers?.length ? <div className="space-y-2">{dashboard.topUsers.map((user, index) => (
            <Link key={user.id} to={`/admin/users?user=${user.id}`} className="flex items-center gap-3 rounded-lg bg-gray-50 p-3 transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-300">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-sm font-bold text-white">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-800">{personName(user)}</p>
                {secondaryCompany(user) && <p className="truncate text-xs text-gray-500">{secondaryCompany(user)}</p>}
                <p className="text-xs text-gray-400">{user.exportCount} exports · {user.loginCount} logins</p>
              </div>
              <span className="text-xs text-gray-400">{relativeDate(user.lastLogin)}</span>
            </Link>
          ))}</div> : <Empty>No user activity yet</Empty>}
        </section>

        <section className="card p-6">
          <div className="mb-4"><SectionHeading title="Popular content" subtitle="Views and exports in the last 30 days" help="Flyers ranked by combined detail-page views and completed downloads, shares, and emails." icon={FileImage} /></div>
          {dashboard?.popularContent?.length ? <div className="space-y-2">{dashboard.popularContent.map((item, index) => (
            <Link key={item.id} to={`/flyers/${item.id}`} className="flex items-center gap-3 rounded-lg bg-gray-50 p-3 transition hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-300">
              <span className="w-6 text-center text-sm font-bold text-primary-600">#{index + 1}</span><p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{item.title}</p>
              <div className="text-right text-xs text-gray-500"><p>{item.views} views</p><p>{item.exports} exports</p></div>
            </Link>
          ))}</div> : <Empty>No content activity yet</Empty>}
        </section>

        <section className="card p-6">
          <div className="mb-4 flex items-center justify-between"><SectionHeading title="Newest users" subtitle={isSuperAdmin ? 'Recently created accounts across the Hub' : 'Recently created accounts assigned to you'} help="The eight most recently created customer accounts. Click a person to open their usage and account controls." /><Link to="/admin/users?segment=new" className="text-sm font-medium text-primary-600">View all</Link></div>
          {dashboard?.newUsers?.length ? <div className="divide-y divide-gray-100">{dashboard.newUsers.map(user => (
            <Link key={user.id} to={`/admin/users?user=${user.id}`} className="flex items-center justify-between gap-3 py-3 transition hover:bg-gray-50"><div className="min-w-0"><p className="truncate text-sm font-semibold text-gray-800">{personName(user)}</p>{secondaryCompany(user) && <p className="truncate text-xs text-gray-500">{secondaryCompany(user)}</p>}</div><span className="shrink-0 text-xs text-gray-400">Joined {relativeDate(user.createdAt)}</span></Link>
          ))}</div> : <Empty>No users assigned yet</Empty>}
        </section>

        <section className="card p-6">
          <div className="mb-4"><SectionHeading title="Follow-up list" subtitle="Users inactive for at least 30 days" help="Accounts whose most recent login was more than 30 days ago, including older accounts that have never logged in." /></div>
          {dashboard?.inactiveUsers?.length ? <div className="divide-y divide-gray-100">{dashboard.inactiveUsers.map(user => (
            <Link key={user.id} to={`/admin/users?user=${user.id}`} className="flex items-center justify-between gap-3 py-3 transition hover:bg-gray-50"><div className="min-w-0"><p className="truncate text-sm font-semibold text-gray-800">{personName(user)}</p>{secondaryCompany(user) && <p className="truncate text-xs text-gray-500">{secondaryCompany(user)}</p>}</div><span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">{relativeDate(user.lastLogin)}</span></Link>
          ))}</div> : <Empty>Everyone has been active recently</Empty>}
        </section>
      </div>

      <section className="card mt-6 p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Bell size={19} className="text-primary-600" /><SectionHeading title="Recent notifications" subtitle={isSuperAdmin ? 'Invite requests and user activity' : 'Account, export, and password-reset activity'} help={isSuperAdmin ? 'Invite code requests and activity alerts. Click an invite request to manage codes. Unread alerts use a colored dot.' : 'Alerts created by activity from users assigned to you. Unread alerts use a colored dot.'} /></div>
          {summary.unreadNotifications > 0 && <button type="button" onClick={markNotificationsRead} className="text-sm font-medium text-primary-600">Mark all read</button>}
        </div>
        {dashboard?.notifications?.length ? <div className="divide-y divide-gray-100">{dashboard.notifications.map(item => (
          item.userId || item.metadata?.flyerId || item.eventType === 'invite_request' ? <Link key={item.id} to={item.eventType === 'invite_request' ? '/admin/invite-codes' : item.userId ? `/admin/users?user=${item.userId}` : `/flyers/${item.metadata.flyerId}`} className="flex gap-3 py-3 transition hover:bg-gray-50"><span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${item.readAt ? 'bg-gray-200' : 'bg-primary-500'}`} /><div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-800">{item.title}</p><p className="break-words text-sm text-gray-600">{item.message}</p></div><span className="shrink-0 text-xs text-gray-400">{relativeDate(item.createdAt)}</span></Link> : <div key={item.id} className="flex gap-3 py-3"><span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${item.readAt ? 'bg-gray-200' : 'bg-primary-500'}`} /><div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-800">{item.title}</p><p className="text-sm text-gray-600">{item.message}</p></div><span className="shrink-0 text-xs text-gray-400">{relativeDate(item.createdAt)}</span></div>
        ))}</div> : <Empty>No notifications yet</Empty>}
      </section>
    </div>
  );
};

export default AdminDashboard;
